import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import KpiCard from '../components/KpiCard';
import SortableTh from '../components/SortableTh';
import TablePagination from '../components/TablePagination';
import { fmtMoney, fmtPct, fmtInt } from '../lib/fmt';
import { compareValues, paginateRows } from '../lib/table';
import { exportTableAsExcel } from '../lib/export';
import type { FornecedorOut, NFOut, PaginatedResponse } from '../types/api';

type SortField = 'nome' | 'total_skus' | 'valor_perdido' | 'valor_sugestao' | 'margem_media' | 'pedidos_abertos' | 'lead_time_medio' | 'lt_real';

function exportFornecedorXLSX(
  fornecedores: FornecedorOut[],
  ltMap: Record<string, { avg: number; count: number; min: number; max: number }>
) {
  const headers = ['Fornecedor / Marca','SKUs','Fat. em risco','Compra sugerida','Margem média (%)','Lead real médio (d)','Lead mín (d)','Lead máx (d)','NFs com lead','POs abertos','Lead cadastrado (d)'];
  const rows = fornecedores.map(f => {
    const lt = ltMap[f.nome];
    return [
      f.nome, f.total_skus,
      f.valor_perdido,
      f.valor_sugestao,
      Number((f.margem_media * 100).toFixed(1)),
      lt ? Number(lt.avg.toFixed(1)) : '',
      lt ? lt.min : '',
      lt ? lt.max : '',
      lt ? lt.count : '',
      f.pedidos_abertos,
      f.lead_time_medio ?? '',
    ];
  });
  exportTableAsExcel(`MBC_fornecedores_${new Date().toISOString().slice(0,10)}.xlsx`, headers, rows);
}

export default function Fornecedor() {
  const [busca,setBusca] = useState('');
  const [sortBy, setSortBy] = useState<SortField>('valor_sugestao');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const pageSize = 15;

  const {data: fornecedoresResponse, isLoading} = useQuery<PaginatedResponse<FornecedorOut>>({
    queryKey:['fornecedores',busca],
    queryFn:()=>{ const p=new URLSearchParams({limit:'500'}); if(busca)p.set('busca',busca); return api.get<PaginatedResponse<FornecedorOut>>(`/analysis/fornecedores?${p}`).then(r=>r.data); },
  });
  const {data:nfsPage} = useQuery<PaginatedResponse<NFOut>>({
    queryKey:['nfs-all'],
    queryFn:()=>api.get<PaginatedResponse<NFOut>>('/nfs?limit=1000').then(r=>r.data),
  });
  const nfs = nfsPage?.items ?? [];

  const fornecedores = fornecedoresResponse?.items ?? [];
  const totalCapital = fornecedores.reduce((s,f)=>s+f.valor_sugestao,0);
  const totalPerdido = fornecedores.reduce((s,f)=>s+f.valor_perdido,0);
  const nfsComLt     = nfs.filter(n=>n.lead_time_dias!=null&&n.lead_time_dias>=0);
  const leadGlobal   = nfsComLt.length ? nfsComLt.reduce((s,n)=>s+(n.lead_time_dias??0),0)/nfsComLt.length : 0;
  const fornComLt    = new Set(nfsComLt.map(n=>n.emitente)).size;

  // build NF lead-time lookup per emitente
  const ltMap: Record<string,{avg:number;count:number;min:number;max:number}> = {};
  nfsComLt.forEach(n=>{ const e=n.emitente; const lt=n.lead_time_dias!; if(!ltMap[e]) ltMap[e]={avg:0,count:0,min:lt,max:lt}; ltMap[e].count++; ltMap[e].avg+=lt; ltMap[e].min=Math.min(ltMap[e].min,lt); ltMap[e].max=Math.max(ltMap[e].max,lt); });
  Object.values(ltMap).forEach(v=>v.avg=v.avg/v.count);

  const sortedRows = [...fornecedores].sort((a, b) => {
    const av = sortBy === 'lt_real' ? ltMap[a.nome]?.avg : a[sortBy];
    const bv = sortBy === 'lt_real' ? ltMap[b.nome]?.avg : b[sortBy];
    return compareValues(av, bv, sortDir);
  });
  const pagedRows = paginateRows(sortedRows, page, pageSize);

  const onSort = (field: SortField) => {
    if (sortBy === field) {
      setSortDir(current => current === 'desc' ? 'asc' : 'desc');
      return;
    }
    setSortBy(field);
    setSortDir(field === 'nome' ? 'asc' : 'desc');
    setPage(1);
  };

  return (
    <div className="space-y-6 fade-in">
      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <KpiCard label="Fornecedores"     value={fmtInt(fornecedores.length)} sub="na operação"           accent="mbc-orangel" icon="🏭" />
        <KpiCard label="Lead time médio NF" value={nfsComLt.length?`${leadGlobal.toFixed(1)}d`:'—'} sub={`${nfsComLt.length} NFs · ${fornComLt} fornec. com histórico`} accent="mbc-bluel" icon="⏱️" />
        <KpiCard label="Capital necessário" value={fmtMoney(totalCapital)}   sub="compra sugerida total" accent="mbc-bluel"   icon="💰" />
        <KpiCard label="Faturamento em risco" value={fmtMoney(totalPerdido)} sub="por ruptura"           accent="rose-300"    icon="⚠️" />
      </div>

      <div className="mb-4">
        <input type="text" placeholder="Buscar fornecedor…" value={busca} onChange={e=>setBusca(e.target.value)}
          className="w-full max-w-sm bg-mbc-panel2 border border-mbc-line rounded-lg px-3 py-2 text-sm text-white placeholder:text-mbc-mute focus:outline-none focus:border-mbc-orange"/>
      </div>

      <div className="card p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-white font-bold">Performance por fornecedor / marca</h3>
          {fornecedores.length > 0 && (
            <button onClick={() => exportFornecedorXLSX(fornecedores, ltMap)} className="text-xs bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 px-3 py-1.5 rounded-lg transition-colors">
              📥 Exportar Excel
            </button>
          )}
        </div>
        <div className="scroller">
          {isLoading ? <div className="p-8 text-center text-mbc-mute">Carregando…</div> :
          fornecedores.length===0 ? <div className="p-8 text-center text-mbc-mute">Nenhum fornecedor encontrado.</div> : (
            <table className="dt">
              <thead>
                <tr>
                  <SortableTh label="Fornecedor / Marca" active={sortBy==='nome'} direction={sortDir} onClick={() => onSort('nome')} className="text-left" style={{width:220}} />
                  <SortableTh label="SKUs" active={sortBy==='total_skus'} direction={sortDir} onClick={() => onSort('total_skus')} className="num" style={{width:60}} />
                  <th className="num" style={{width:90}}>Ruptura (un)</th>
                  <SortableTh label="Fat. em risco" active={sortBy==='valor_perdido'} direction={sortDir} onClick={() => onSort('valor_perdido')} className="num" style={{width:130}} />
                  <SortableTh label="Compra sugerida" active={sortBy==='valor_sugestao'} direction={sortDir} onClick={() => onSort('valor_sugestao')} className="num" style={{width:130}} />
                  <SortableTh label="Margem média" active={sortBy==='margem_media'} direction={sortDir} onClick={() => onSort('margem_media')} className="num" style={{width:100}} />
                  <SortableTh label="⏱️ Lead real médio" active={sortBy==='lt_real'} direction={sortDir} onClick={() => onSort('lt_real')} className="num" style={{width:140}} />
                  <th className="num" style={{width:90}}>Min/Máx</th>
                  <SortableTh label="POs abertos" active={sortBy==='pedidos_abertos'} direction={sortDir} onClick={() => onSort('pedidos_abertos')} className="num" style={{width:70}} />
                  <SortableTh label="Regra cadastrada" active={sortBy==='lead_time_medio'} direction={sortDir} onClick={() => onSort('lead_time_medio')} style={{width:160}} />
                </tr>
              </thead>
              <tbody>
                {pagedRows.map(f=>{
                  const lt=ltMap[f.nome];
                  const ltColor=lt?(lt.avg<=7?'text-emerald-300':lt.avg<=15?'text-mbc-bluel':lt.avg<=30?'text-amber-300':'text-rose-300'):'';
                  return (
                    <tr key={f.nome}>
                      <td><span className="text-white font-medium truncate inline-block max-w-[200px]">{f.nome}</span></td>
                      <td className="num">{fmtInt(f.total_skus)}</td>
                      <td className="num text-mbc-mute">—</td>
                      <td className="num text-rose-300">{fmtMoney(f.valor_perdido)}</td>
                      <td className="num text-emerald-300 font-semibold">{fmtMoney(f.valor_sugestao)}</td>
                      <td className="num"><span className={f.margem_media>=0.10?'text-emerald-300':'text-amber-400'}>{fmtPct(f.margem_media)}</span></td>
                      <td className="num">
                        {lt
                          ? <><span className={`font-bold ${ltColor}`}>{lt.avg.toFixed(1)}d</span> <span className="text-mbc-mute text-[10px]">({lt.count} NF{lt.count>1?'s':''})</span></>
                          : <span className="text-mbc-mute text-xs">sem NF</span>}
                      </td>
                      <td className="num text-mbc-mute text-[10px]">{lt?`${lt.min}d / ${lt.max}d`:'—'}</td>
                      <td className="num">{f.pedidos_abertos>0?<span className="text-mbc-orange font-semibold">{f.pedidos_abertos}</span>:<span className="text-mbc-mute">—</span>}</td>
                      <td>
                        {f.lead_time_medio!=null
                          ? <span className="badge bg-emerald-500/20 text-emerald-300">lead {f.lead_time_medio.toFixed(0)}d</span>
                          : <span className="badge bg-slate-500/20 text-slate-400">sem cadastro</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        <TablePagination total={fornecedores.length} page={page} pageSize={pageSize} onPageChange={setPage} />
      </div>
    </div>
  );
}
